import type { ReactNode } from 'react';
import { KindeProvider, useKindeAuth } from '@kinde-oss/kinde-auth-react';

import { kindeConfig } from './config';
import { AuthProvider } from './context';
import type { AuthContextType, User } from './context';

function AuthBridge({ children }: { children: ReactNode }) {
  const kinde = useKindeAuth();

  const user: User | null = kinde.user
    ? {
        id: kinde.user.id ?? '',
        email: kinde.user.email ?? null,
        name: [kinde.user.givenName, kinde.user.familyName].filter(Boolean).join(' ') || null,
        avatarUrl: kinde.user.picture ?? null,
      }
    : null;

  const handleLogout = async () => {
    // Clear local session and redirect to Kinde logout
    const logoutUrl = `${kindeConfig.domain}/logout?redirect=${encodeURIComponent(kindeConfig.logoutRedirectUri)}`;
    window.location.href = logoutUrl;
  };

  const value: AuthContextType = {
    isAuthenticated: kinde.isAuthenticated ?? false,
    isLoading: kinde.isLoading,
    isInitialized: !kinde.isLoading,
    user,
    login: kinde.login,
    logout: handleLogout,
    register: kinde.register,
    getAccessToken: kinde.getAccessToken,
  };

  return <AuthProvider value={value}>{children}</AuthProvider>;
}

export function AuthProviderWrapper({ children }: { children: ReactNode }) {
  return (
    <KindeProvider
      clientId={kindeConfig.clientId}
      domain={kindeConfig.domain}
      redirectUri={kindeConfig.redirectUri}
      logoutUri={kindeConfig.logoutRedirectUri}
      audience={kindeConfig.audience}
    >
      <AuthBridge>{children}</AuthBridge>
    </KindeProvider>
  );
}
